import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DepreciationCalculator } from '@/lib/depreciation-calculator';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Play,
  FileText,
  CheckCircle2,
  Clock,
  Sparkles,
  Download,
  Eye,
  Save,
  X,
  RotateCcw,
  FileSpreadsheet,
  MoreVertical,
  Share2,
  Plus,
  Trash2,
  Settings2,
  TrendingDown,
  Search,
} from 'lucide-react';
import { AssetSelectionDialog } from '@/components/depreciation/asset-selection-dialog';
import { BatchDetailGrid } from '@/components/depreciation/batch-detail-grid';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import { DemoModeWarningModal } from '@/components/ui/demo-mode-warning-modal';
import type { Asset } from '@/types/asset';
import type { DepreciationBatch, DepreciationItem } from '@/types/depreciation';
import { mockCurrentPeriod, mockProjects, mockCompletedBatches } from '@/lib/mock-depreciation-data';
import { mockAssets } from '@/lib/mock-data';
import { dataService } from '@/services/data-service';
import { useMode } from '@/providers/ModeProvider';
import { exportToExcel, generateFilename, formatCurrencyForExport, formatDateForExport, type ExportColumn } from '@/lib/export-utils';

export default function DepreciationPage(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDemo, setMode } = useMode();
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<DepreciationBatch | null>(null);
  const [period] = useState(mockCurrentPeriod);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [showModifiedHighlight, setShowModifiedHighlight] = useState(false);
  const [completedBatches, setCompletedBatches] = useState<DepreciationBatch[]>(mockCompletedBatches);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showDemoWarning, setShowDemoWarning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchBatches = async () => {
      setIsLoadingBatches(true);
      try {
        const batches = await dataService.getDepreciationBatches();
        if (batches && batches.length > 0) {
          const sortedBatches = batches.sort((a: any, b: any) => 
            new Date(b.dateOfRun).getTime() - new Date(a.dateOfRun).getTime()
          );
          setCompletedBatches(sortedBatches);
        } else {
          setCompletedBatches(mockCompletedBatches);
        }
      } catch (error) {
        console.error('Error fetching depreciation batches:', error);
        setCompletedBatches(mockCompletedBatches);
      } finally {
        setIsLoadingBatches(false);
      }
    };

    if (!currentBatch) {
      fetchBatches();
    }
  }, [currentBatch]);

  // Handle selected assets passed from assets page
  useEffect(() => {
    const state = location.state as { selectedAssets?: Asset[] } | null;
    if (state?.selectedAssets && state.selectedAssets.length > 0) {
      handleAssetsSelected(state.selectedAssets);
      // Clear the state to prevent re-triggering on navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleStartRun = () => {
    if (isDemo) {
      setShowDemoWarning(true);
      return;
    }
    setShowAssetSelection(true);
  };

  const handleStartRunWithAllAssets = () => {
    if (isDemo) {
      setShowDemoWarning(true);
      return;
    }
    // Auto-select all assets from mock data and start the run
    handleAssetsSelected(mockAssets);
  };

  const handleAddMoreAssets = () => {
    setShowAssetSelection(true);
  };

  const handleRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    setShowRemoveConfirm(true);
  };

  const confirmRemoveSelectedItems = () => {
    if (!currentBatch || selectedItemIds.size === 0) return;
    
    const updatedItems = currentBatch.items.filter(item => !selectedItemIds.has(item.id));
    
    const totalDepreciation = updatedItems.reduce((sum, item) => sum + item.depreciationValue, 0);
    const totalOpeningNBV = updatedItems.reduce((sum, item) => sum + item.openingNBV, 0);
    const totalClosingNBV = updatedItems.reduce((sum, item) => sum + item.closingNBV, 0);
    
    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalDepreciation,
      totalOpeningNBV,
      totalClosingNBV,
    });
    
    setSelectedItemIds(new Set());
  };

  const handleAssetsSelected = (selectedAssets: Asset[]) => {
    // Create depreciation items from selected assets
    const newItems: DepreciationItem[] = selectedAssets.map((asset, index) => {
      // Use the DepreciationCalculator to calculate depreciation based on asset's method
      const calculation = DepreciationCalculator.calculateDepreciation(asset, new Date(), 12);
      
      const usefulLifeYears = asset.usefulLife || 5;
      const depreciationMethod = asset.depreciationMethod || 'Straight Line';

      return {
        id: `dep-item-${Date.now()}-${index}`,
        assetId: asset.id,
        assetCode: asset.assetId,
        assetDescription: asset.name,
        category: asset.category,
        location: asset.location,
        openingNBV: asset.netBookValue,
        acquisitionCost: asset.acquisitionCost,
        accumulatedDepreciation: asset.accumulatedDepreciation,
        method: depreciationMethod as any,
        usefulLife: usefulLifeYears,
        lifeRemaining: usefulLifeYears - (asset.accumulatedDepreciation / asset.acquisitionCost) * usefulLifeYears,
        residualValue: asset.residualValue || 0,
        aiDepreciationPercent: calculation.depreciationPercent || 0,
        aiDepreciationValue: calculation.depreciationValue,
        aiClosingNBV: calculation.closingNBV,
        confidence: 'High' as const,
        depreciationPercent: calculation.depreciationPercent || 0,
        depreciationValue: calculation.depreciationValue,
        closingNBV: calculation.closingNBV,
        isManualOverride: false,
        validationStatus: 'valid',
        calculationInputs: {
          projectMethod: depreciationMethod as any,
          usefulLife: usefulLifeYears,
          residualValue: asset.residualValue || 0,
          proRataConvention: 'Full Month',
          startDateRule: 'In-Service Date',
          inServiceDate: asset.acquisitionDate,
          periodFraction: 1,
          formula: calculation.formula,
          dataSources: ['Asset Master'],
          assumptions: calculation.assumptions,
        },
      };
    });

    // If we have an existing batch, add to it; otherwise create new batch
    if (currentBatch) {
      // Filter out duplicates based on assetId
      const existingAssetIds = new Set(currentBatch.items.map(item => item.assetId));
      const uniqueNewItems = newItems.filter(item => !existingAssetIds.has(item.assetId));
      
      const allItems = [...currentBatch.items, ...uniqueNewItems];
      const totalDepreciation = allItems.reduce((sum, item) => sum + item.depreciationValue, 0);
      const totalOpeningNBV = allItems.reduce((sum, item) => sum + item.openingNBV, 0);
      const totalClosingNBV = allItems.reduce((sum, item) => sum + item.closingNBV, 0);
      
      setCurrentBatch({
        ...currentBatch,
        items: allItems,
        totalDepreciation,
        totalOpeningNBV,
        totalClosingNBV,
      });
    } else {
      // Create new batch
      const totalDepreciation = newItems.reduce((sum, item) => sum + item.depreciationValue, 0);
      const totalOpeningNBV = newItems.reduce((sum, item) => sum + item.openingNBV, 0);
      const totalClosingNBV = newItems.reduce((sum, item) => sum + item.closingNBV, 0);

      const batch: DepreciationBatch = {
        id: `batch-${Date.now()}`,
        batchReference: '',
        dateOfRun: new Date().toISOString().split('T')[0],
        projectId: '',
        projectName: '',
        status: 'In Progress',
        notes: '',
        items: newItems,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        modifiedBy: 'Current User',
        modifiedDate: new Date().toISOString(),
        preparedBy: 'Current User',
        preparedDate: new Date().toISOString(),
        currentStep: 'Prepare',
        totalDepreciation,
        totalOpeningNBV,
        totalClosingNBV,
        currency: 'GBP',
      };

      setCurrentBatch(batch);
    }
    
    setShowAssetSelection(false);
  };

  const handleUpdateItem = (itemId: string, updates: Partial<DepreciationItem>) => {
    if (!currentBatch) return;

    const updatedItems = currentBatch.items.map((item) =>
      item.id === itemId ? { ...item, ...updates } : item
    );

    const totalDepreciation = updatedItems.reduce((sum, item) => sum + item.depreciationValue, 0);
    const totalClosingNBV = updatedItems.reduce((sum, item) => sum + item.closingNBV, 0);

    setCurrentBatch({
      ...currentBatch,
      items: updatedItems,
      totalDepreciation,
      totalClosingNBV,
    });
  };

  const isServerGeneratedId = (id: string) => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(id);
  };

  const handleSave = async () => {
    if (!currentBatch) return;
    
    try {
      const now = new Date().toISOString();
      const batchToSave = {
        ...currentBatch,
        modifiedBy: 'Current User',
        modifiedDate: now,
        status: currentBatch.status || 'In Progress',
      };
      
      if (currentBatch.id && isServerGeneratedId(currentBatch.id)) {
        const updatedBatch = await dataService.updateDepreciationBatch(currentBatch.id, batchToSave);
        setCurrentBatch({
          ...updatedBatch,
          dateOfRun: updatedBatch.dateOfRun?.split('T')[0] || updatedBatch.dateOfRun,
        });
      } else {
        const savedBatch = await dataService.createDepreciationBatch(batchToSave);
        setCurrentBatch({
          ...savedBatch,
          dateOfRun: savedBatch.dateOfRun?.split('T')[0] || savedBatch.dateOfRun,
        });
      }
      
      setShowModifiedHighlight(true);
      setTimeout(() => setShowModifiedHighlight(false), 2000);
      
      toast.success('Batch saved', { description: 'Depreciation batch saved successfully' });
    } catch (error) {
      console.error('Failed to save depreciation batch:', error);
      toast.error('Failed to save batch', { description: 'Please ensure you are in API mode and try again.' });
    }
  };

  const handleProcess = () => {
    if (!currentBatch) return;
    
    // Validate mandatory fields
    if (!currentBatch.batchReference || !currentBatch.batchReference.trim()) {
      toast.error('Validation Error', {
        description: 'Reference is required before processing',
      });
      return;
    }
    
    if (!currentBatch.dateOfRun) {
      toast.error('Validation Error', {
        description: 'Date is required before processing',
      });
      return;
    }
    
    setShowProcessConfirm(true);
  };

  const confirmProcess = async () => {
    if (!currentBatch) return;
    
    const reference = currentBatch.batchReference;
    
    try {
      // Create a transaction for each item in the batch
      for (const item of currentBatch.items) {
        await dataService.createTransaction({
          transactionDate: currentBatch.dateOfRun,
          type: 'depreciation',
          assetId: item.assetId,
          amount: item.depreciationValue,
          currency: currentBatch.currency,
          status: 'posted',
          reference: currentBatch.batchReference,
          description: `Depreciation charge - ${currentBatch.batchReference || 'Batch'}`,
          preparedBy: currentBatch.preparedBy,
          preparedDate: currentBatch.preparedDate,
          postedBy: 'Current User',
          postedDate: new Date().toISOString(),
        });
      }
      
      // Save the batch to the database with completed status
      const completedBatch = {
        ...currentBatch,
        status: 'Completed',
        postedBy: 'Current User',
        postedDate: new Date().toISOString(),
      };
      
      // Create or update the batch in the database
      if (currentBatch.id && isServerGeneratedId(currentBatch.id)) {
        await dataService.updateDepreciationBatch(currentBatch.id, completedBatch);
      } else {
        await dataService.createDepreciationBatch(completedBatch);
      }
      
      // Show success toast
      toast.success('Depreciation Created Successfully', {
        description: `Reference: ${reference}`,
      });
      
      // Clear batch and navigate back to processing screen
      setCurrentBatch(null);
      setSelectedItemIds(new Set());
      setShowProcessConfirm(false);
      
      // Navigate back to processing screen
      navigate('/transactions/depreciation');
    } catch (error) {
      console.error('Failed to process depreciation batch:', error);
      toast.error('Failed to process depreciation batch', {
        description: 'Please ensure you are in API mode and try again.',
      });
      setShowProcessConfirm(false);
    }
  };

  const handleCancelBatch = () => {
    setShowCancelConfirm(true);
  };

  const confirmCancelBatch = () => {
    setCurrentBatch(null);
  };

  const handleExportExcel = () => {
    if (!currentBatch || !currentBatch.items || currentBatch.items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      const columns: ExportColumn<DepreciationItem>[] = [
        { label: 'Asset Code', key: 'assetCode' as keyof DepreciationItem },
        { label: 'Asset Description', key: 'assetDescription' as keyof DepreciationItem },
        { label: 'Category', key: 'category' as keyof DepreciationItem },
        { label: 'Location', key: 'location' as keyof DepreciationItem },
        { label: 'Opening NBV', key: 'openingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Acquisition Cost', key: 'acquisitionCost' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Accumulated Depreciation', key: 'accumulatedDepreciation' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Method', key: 'method' as keyof DepreciationItem },
        { label: 'Useful Life (Years)', key: 'usefulLife' as keyof DepreciationItem },
        { label: 'Life Remaining (Years)', key: 'lifeRemaining' as keyof DepreciationItem, format: (val: number) => val.toFixed(2) },
        { label: 'Residual Value', key: 'residualValue' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Depreciation %', key: 'depreciationPercent' as keyof DepreciationItem, format: (val: number) => val ? `${val.toFixed(2)}%` : '' },
        { label: 'Depreciation Value', key: 'depreciationValue' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Closing NBV', key: 'closingNBV' as keyof DepreciationItem, format: (val: number) => formatCurrencyForExport(val) },
        { label: 'Manual Override', key: 'isManualOverride' as keyof DepreciationItem, format: (val: boolean) => val ? 'Yes' : 'No' },
        { label: 'Override Reason', key: 'overrideReason' as keyof DepreciationItem },
        { label: 'Notes', key: 'notes' as keyof DepreciationItem },
      ];

      exportToExcel({
        data: currentBatch.items,
        columns,
        filename: generateFilename(`depreciation-batch-${currentBatch.batchReference || 'draft'}`, 'xls'),
        sheetName: 'Depreciation Details',
      });

      toast.success('Export successful', {
        description: 'Depreciation batch exported to Excel',
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  // Calculate estimated period depreciation from all assets
  const estimatedPeriodDepreciation = mockAssets.reduce((sum: any, asset: any) => {
    const usefulLifeYears = 5;
    const monthlyRate = 1 / (usefulLifeYears * 12);
    const depreciation = (asset.netBookValue - (asset.residualValue || 0)) * monthlyRate;
    return sum + depreciation;
  }, 0);

  // Calculate current total NBV from all assets
  const currentTotalNBV = mockAssets.reduce((sum: any, asset: any) => sum + asset.netBookValue, 0);
  
  // Calculate expected NBV after depreciation
  const expectedTotalNBV = currentTotalNBV - estimatedPeriodDepreciation;

  // Landing Dashboard View
  if (!currentBatch) {
    return (
      <div className="flex h-full flex-col">
        {/* Page Header */}
        <div className="border-b bg-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <TrendingDown className="h-6 w-6 text-primary" />
                Depreciation Processing
              </h1>
              <p className="text-sm text-muted-foreground mt-1">AI-driven bulk calculation and posting of asset depreciation</p>
            </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowComingSoon(true)}>
              <FileText className="mr-2 h-4 w-4" />
              View Last Run Summary
            </Button>
            <Button onClick={handleStartRun}>
              <Play className="mr-2 h-4 w-4" />
              Start New Depreciation Run
            </Button>
          </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="space-y-6 p-6">
        {/* Current Period Status - Wrapped with gradient border */}
        <div>
          <div 
            className="rounded-xl p-[5px]"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            }}
          >
            <Card className="border-0 rounded-[10px] relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 opacity-40" />
              <CardContent className="relative p-6 space-y-6">
                <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-semibold">Current Period: {period.period}</h2>
                      <Sparkles className="h-5 w-5 text-purple-500" />
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Suggested depreciation run for current period</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-4 right-4 h-8 w-8"
                    onClick={() => toast.info('Close functionality coming soon')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                <div className="grid grid-cols-3 gap-4 2xl:gap-6">
                  <div className="space-y-1">
                    <div className="text-xs 2xl:text-sm text-muted-foreground">Assets in Scope</div>
                    <div className="text-lg 2xl:text-2xl font-bold">{mockAssets.length.toLocaleString()}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs 2xl:text-sm text-muted-foreground">Current NBV</div>
                    <div className="text-lg 2xl:text-2xl font-bold">£{currentTotalNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs 2xl:text-sm text-muted-foreground">Expected NBV</div>
                    <div className="text-lg 2xl:text-2xl font-bold">
                      £{expectedTotalNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      <span className="text-xs 2xl:text-sm text-red-600 ml-1 2xl:ml-2 block 2xl:inline">
                        - £{estimatedPeriodDepreciation.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {period.expectedCompletionBy && (
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm text-muted-foreground">Expected Completion By</div>
                      <div className="text-sm font-medium">
                        {new Date(period.expectedCompletionBy).toLocaleDateString('en-GB', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </div>
                    </div>
                    {period.status === 'Not Started' && (
                      <Button size="lg" onClick={handleStartRunWithAllAssets}>
                        <Play className="mr-2 h-5 w-5" />
                        Start Depreciation Run
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {period.status === 'Completed' && period.completedBy && (
                <div className="pt-4 border-t">
                  <div className="text-sm text-muted-foreground">Completed</div>
                  <div className="text-sm font-medium">
                    {period.completedBy} on {period.completedDate && new Date(period.completedDate).toLocaleString('en-GB')}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        </div>

        {/* Transaction History */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold">Transaction History</h2>
              <p className="text-sm text-muted-foreground mt-1">View and manage all depreciation transactions</p>
            </div>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                className="pl-9 bg-background"
              />
            </div>
          </div>
          
          <div className="space-y-3">
            {isLoadingBatches ? (
              <div className="text-center py-8 text-muted-foreground">Loading transactions...</div>
            ) : completedBatches.filter((batch) => {
              if (!searchQuery) return true;
              const query = searchQuery.toLowerCase();
              return (
                batch.batchReference.toLowerCase().includes(query) ||
                batch.status.toLowerCase().includes(query) ||
                (batch.postedBy && batch.postedBy.toLowerCase().includes(query))
              );
            }).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No transactions found</div>
            ) : (
              completedBatches.filter((batch) => {
                if (!searchQuery) return true;
                const query = searchQuery.toLowerCase();
                return (
                  batch.batchReference.toLowerCase().includes(query) ||
                  batch.status.toLowerCase().includes(query) ||
                  (batch.postedBy && batch.postedBy.toLowerCase().includes(query))
                );
              }).map((batch) => (
                <div key={batch.id} className="flex items-center justify-between rounded-lg border border-border p-4 bg-background hover:bg-muted/50 transition-colors shadow-sm">
                  <div className="space-y-1">
                    <div className="font-semibold">{batch.batchReference}</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(batch.dateOfRun).toLocaleDateString('en-GB')} • £
                      {batch.totalDepreciation.toLocaleString('en-GB', { minimumFractionDigits: 2 })} • {batch.items?.length || 0} assets
                    </div>
                    {batch.postedBy && batch.postedDate && (
                      <div className="text-xs text-muted-foreground">
                        Posted by {batch.postedBy} on {new Date(batch.postedDate).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="secondary"
                      className={
                        batch.status === 'In Progress'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      }
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {batch.status}
                    </Badge>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        // Format the date to YYYY-MM-DD for the date input
                        const formattedBatch = {
                          ...batch,
                          dateOfRun: batch.dateOfRun.split('T')[0]
                        };
                        setCurrentBatch(formattedBatch);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

          </div>
        </div>

        <AssetSelectionDialog
          open={showAssetSelection}
          onOpenChange={setShowAssetSelection}
          onConfirm={handleAssetsSelected}
          excludeAssetIds={[]}
        />

        <ComingSoonModal
          open={showComingSoon}
          onOpenChange={setShowComingSoon}
          title="Run Summary Coming Soon"
          description="The ability to view the last run summary will be available in an upcoming release."
        />

        <DemoModeWarningModal
          open={showDemoWarning}
          onOpenChange={setShowDemoWarning}
          onSwitchToLive={() => setMode('live')}
        />
      </div>
    );
  }

  // Batch Processing View
  const readonly = currentBatch.status === 'Completed';

  return (
    <div className="relative flex flex-col h-full">
      <div className="flex-1 overflow-auto pb-32">
      {/* Page Header */}
      <div className="bg-card p-6 pb-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <TrendingDown className="h-6 w-6 text-primary" />
              Asset Depreciation
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Last Modified{' '}
              <span 
                className={`font-semibold transition-colors duration-500 ${
                  showModifiedHighlight ? 'text-green-600 dark:text-green-400' : 'text-foreground'
                }`}
              >
                {currentBatch.modifiedDate ? new Date(currentBatch.modifiedDate).toLocaleString('en-GB', { 
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }).replace(/(\d+)/, (match) => {
                  const day = parseInt(match);
                  const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                                 day === 2 || day === 22 ? 'nd' : 
                                 day === 3 || day === 23 ? 'rd' : 'th';
                  return `${day}${suffix}`;
                }) : 'Never'}
              </span>
            </p>
          </div>
          <Badge
            variant="secondary"
            className={`text-base px-3 py-1 ${
              currentBatch.status === 'In Progress'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            }`}
          >
            {currentBatch.status === 'Completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
            {currentBatch.status === 'In Progress' && <Clock className="h-3 w-3 mr-1" />}
            {currentBatch.status}
          </Badge>
        </div>
        
        <div className="-mx-6 px-6 pt-6 pb-4 border-y border-border bg-[#F9FAFB] dark:bg-[#121212]">
          <div className="space-y-4">
            <div className="flex gap-4">
              {/* Left side: Reference and Date stacked */}
              <div className="flex flex-col gap-4 flex-shrink-0" style={{ width: '300px' }}>
                <div className="space-y-2">
                  <Label htmlFor="batch-ref">
                    Reference <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="batch-ref" 
                    value={currentBatch.batchReference} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, batchReference: e.target.value })}
                    readOnly={readonly} 
                    placeholder="Enter reference"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date-of-run">
                    Date <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    id="date-of-run" 
                    type="date" 
                    value={currentBatch.dateOfRun} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, dateOfRun: e.target.value })}
                    readOnly={readonly} 
                  />
                </div>
              </div>

              {/* Right side: Notes textarea spanning full height */}
              <div className="flex-1 flex flex-col">
                <Label htmlFor="notes" className="mb-2">Notes</Label>
                <Textarea
                  id="notes"
                  value={currentBatch.notes}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCurrentBatch({ ...currentBatch, notes: e.target.value })}
                  placeholder="Add notes about this depreciation run..."
                  className="flex-1 resize-none h-[104px] bg-white dark:bg-gray-950"
                  readOnly={readonly}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Grid */}
      <div 
        className="bg-card" 
        style={{ 
          paddingLeft: 'calc(var(--spacing) * 6)',
          paddingRight: 'calc(var(--spacing) * 6)',
          paddingTop: 'calc(var(--spacing) * 6)',
          paddingBottom: 'calc(var(--spacing) * 6)'
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">Depreciation Details</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {currentBatch.items.length} {currentBatch.items.length === 1 ? 'asset' : 'assets'} added to this batch
            </p>
          </div>
          {!readonly && (
            <div className="flex items-center gap-3">
              {/* AI Calculation Info Message */}
              <div 
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm"
                style={{
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 50%, rgba(240, 147, 251, 0.1) 100%)',
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                }}
              >
                <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
                <span className="text-muted-foreground">Depreciation values have been automatically calculated</span>
              </div>
              
              <Button variant="default" className="h-[42px]" onClick={handleAddMoreAssets}>
                <Plus className="h-4 w-4 mr-2" />
                Add Assets
              </Button>
              {selectedItemIds.size > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Settings2 className="h-4 w-4 mr-2" />
                      Actions ({selectedItemIds.size})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleRemoveSelectedItems} className="text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove Selected
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="border-y border-border min-h-[calc(100vh-400px)] bg-[#F9FAFB] dark:bg-[#121212]" style={{ 
        paddingLeft: 'calc(var(--spacing) * 6)', 
        paddingRight: 'calc(var(--spacing) * 6)',
        paddingTop: '10px',
        paddingBottom: '10px'
      }}>
        
        <BatchDetailGrid 
          items={currentBatch.items} 
          onUpdateItem={handleUpdateItem} 
          readonly={readonly}
          selectedItemIds={selectedItemIds}
          onSelectionChange={setSelectedItemIds}
        />
      </div>
      
      <AssetSelectionDialog
        open={showAssetSelection}
        onOpenChange={setShowAssetSelection}
        onConfirm={handleAssetsSelected}
        excludeAssetIds={currentBatch?.items.map(item => item.assetId) || []}
      />

      {/* Confirmation Modals */}
      <ConfirmationModal
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        onConfirm={confirmCancelBatch}
        title="Cancel Depreciation Batch?"
        description="Are you sure you want to cancel this batch? All unsaved changes will be lost and cannot be recovered."
        confirmText="Yes, Cancel Batch"
        cancelText="Keep Working"
        type="warning"
      />

      <ConfirmationModal
        open={showProcessConfirm}
        onOpenChange={setShowProcessConfirm}
        onConfirm={confirmProcess}
        title="Process Depreciation Batch"
        description={
          <div className="space-y-2">
            <p>This will post the depreciation transactions to the ledger.</p>
            <div className="mt-4 p-3 bg-muted rounded-md space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Assets:</span>
                <span className="font-medium">{currentBatch?.items.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Depreciation:</span>
                <span className="font-medium">£{currentBatch?.totalDepreciation.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3">This action cannot be undone.</p>
          </div>
        }
        confirmText="Process Batch"
        cancelText="Review Again"
        type="success"
      />

      <ConfirmationModal
        open={showRemoveConfirm}
        onOpenChange={setShowRemoveConfirm}
        onConfirm={confirmRemoveSelectedItems}
        title="Remove Selected Assets"
        description={`Are you sure you want to remove ${selectedItemIds.size} selected asset(s) from this batch? This action cannot be undone.`}
        confirmText="Remove Assets"
        cancelText="Cancel"
        type="danger"
      />

      </div>

      {/* Sticky Footer - Pinned to bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-background border-t shadow-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Action Buttons */}
            <div className="flex gap-2">
              {currentBatch.status === 'In Progress' && (
                <>
                  <Button variant="outline" onClick={handleCancelBatch}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button variant="outline" onClick={handleSave}>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={handleProcess}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Process
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top">
                      <DropdownMenuItem onClick={handleExportExcel}>
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Export to Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
              {currentBatch.status === 'Completed' && (
                <>
                  <Button variant="outline" onClick={() => setCurrentBatch(null)}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Back to Dashboard
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top">
                      <DropdownMenuItem onClick={handleExportExcel}>
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Export to Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>

            {/* Center: Spacer */}
            <div className="flex-1" />

            {/* Right: Summary Totals */}
            <div className="flex gap-8">
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total Opening NBV</div>
                <div className="text-lg font-bold">
                  £{currentBatch.totalOpeningNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total Depreciation</div>
                <div className="text-lg font-bold text-red-600">
                  £{currentBatch.totalDepreciation.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Total Closing NBV</div>
                <div className="text-lg font-bold">
                  £{currentBatch.totalClosingNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
